<template>
  <div class="w-full">
    <div
      v-show="!citationsIsLoading"
      class="flex items-center justify-between mb-4"
    >
      <span class="text-xs text-gray-40 font-bold">{{ $t('citations.sources') }}</span>
      <span class="flex items-center gap-x-3">
        <button
          type="button"
          class="btn btn-primary-dark"
          :disabled="isFirst"
          @click="sourcesPrev"
        >
          <IconsChevronLeft class="h-4 w-4" />
        </button>
        <span class="mt-[1px] text-xs leading-none text-gray-30">{{ currentPage + 1 }}/{{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-primary-dark"
          :disabled="isLast"
          @click="sourcesNext"
        >
          <IconsChevronRight class="h-4 w-4" />
        </button>
      </span>
    </div>

    <div
      class="relative flex items-center justify-center overflow-hidden md:overflow-visible min-h-20"
      v-if="citationItems"
      v-touch:swipe.left="sourcesNext"
      v-touch:swipe.right="sourcesPrev"
    >
      <div
        class="overflow-x-hidden flex"
        ref="itemsContainer"
      >
        <div
          v-for="(item, index) in citationItems"
          :key="index"
          class="shrink-0 grow-0"
          :style="
            index === 0
              ? {
                  ...itemStyle,
                  marginLeft: '0'
                }
              : itemStyle
          "
        >
          <ChatCarouselItem :item="item" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, useTemplateRef } from 'vue'
import ChatCarouselItem from '@/components/chat/ChatCarouselItem.vue'
import { useQuery } from '@tanstack/vue-query'
import { BaseApiCitation } from '@/types/api'
import { AxiosError } from 'axios'
import { useCarousel } from '@/composables/useCarousel'
import { useCitationsStore } from '@/stores/citationsStore'
import { useGrafanaFaro } from '@/composables/useGrafanaFaro'
import Clarity from '@microsoft/clarity'

const { getCitations } = useCitationsStore()
const { faroInstance } = useGrafanaFaro()

export interface ChatCarouselProps {
  citations: number[]
}

const props = defineProps<ChatCarouselProps>()

const {
  isLoading: citationsIsLoading,
  isError: citationsIsError,
  data: citationItems,
  error: citationsError
} = useQuery<BaseApiCitation[], AxiosError>({
  queryKey: ['citations', props.citations.join(',')],
  staleTime: 5 * 60 * 1000,
  queryFn: async () => await getCitations(props.citations)
})

watch(citationsIsError, () => {
  if (citationsIsError) {
    faroInstance.api.pushError(citationsError.value!)
  }
})

const itemsCount = computed(() => citationItems.value?.length ?? 0)
const itemsVisible = computed(() => 1)
const itemsContainerRef = useTemplateRef<HTMLDivElement>('itemsContainer')
const { prev, next, isFirst, isLast, currentPage, totalPages, itemStyle } = useCarousel(
  itemsContainerRef,
  itemsCount,
  itemsVisible,
  0
)

const sourcesNext = () => {
  Clarity.event('clickedSourcesNext')
  next()
}

const sourcesPrev = () => {
  Clarity.event('clickedSourcesPrev')
  prev()
}
</script>
